import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Shield, Plus, Trash2, Edit2, X, LogOut, Loader2, Save, KeyRound } from "lucide-react";
import { Toaster, toast } from "sonner";
import ImageUploadField from "../components/ImageUploadField";
import ChangePasswordDialog from "../components/ChangePasswordDialog";

const TABS = [
  { id: "sliders", label: "Sliders" },
  { id: "news", label: "News" },
  { id: "gallery", label: "Gallery" },
  { id: "events", label: "Events" },
  { id: "messages", label: "Messages" },
  { id: "subscribers", label: "Subscribers" },
];

const emptyTri = { en: "", kar: "", my: "" };

const SCHEMAS = {
  sliders: {
    list: "/admin/sliders",
    create: "/admin/sliders",
    update: (id) => `/admin/sliders/${id}`,
    remove: (id) => `/admin/sliders/${id}`,
    initial: () => ({
      title: { ...emptyTri },
      subtitle: { ...emptyTri },
      image_url: "",
      cta_text: { ...emptyTri },
      cta_link: "",
      order: 1,
      active: true,
    }),
  },
  news: {
    list: "/admin/news",
    create: "/admin/news",
    update: (id) => `/admin/news/${id}`,
    remove: (id) => `/admin/news/${id}`,
    initial: () => ({
      title: { ...emptyTri },
      excerpt: { ...emptyTri },
      content: { ...emptyTri },
      image_url: "",
      category: "General",
      published: true,
    }),
  },
  gallery: {
    list: "/admin/gallery",
    create: "/admin/gallery",
    update: null,
    remove: (id) => `/admin/gallery/${id}`,
    initial: () => ({
      caption: { ...emptyTri },
      image_url: "",
      category: "General",
    }),
  },
  events: {
    list: "/admin/events",
    create: "/admin/events",
    update: (id) => `/admin/events/${id}`,
    remove: (id) => `/admin/events/${id}`,
    initial: () => ({
      title: { ...emptyTri },
      description: { ...emptyTri },
      event_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16),
      location: "",
      image_url: "",
    }),
  },
  messages: {
    list: "/admin/messages",
    create: null,
    update: null,
    remove: (id) => `/admin/messages/${id}`,
    initial: null,
  },
  subscribers: {
    list: "/admin/subscribers",
    create: null,
    update: null,
    remove: (id) => `/admin/subscribers/${id}`,
    initial: null,
  },
};

function TriInput({ label, value, onChange, multiline = false, testid }) {
  const C = multiline ? "textarea" : "input";
  const langs = [
    { k: "en", l: "English" },
    { k: "kar", l: "Karen" },
    { k: "my", l: "Burmese" },
  ];
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">{label}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {langs.map((lng) => (
          <div key={lng.k}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{lng.l}</div>
            <C
              value={value?.[lng.k] || ""}
              onChange={(e) => onChange({ ...value, [lng.k]: e.target.value })}
              rows={multiline ? 4 : undefined}
              data-testid={`${testid}-${lng.k}`}
              className={`w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900 ${
                lng.k === "my" ? "font-burmese" : ""
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">{label}</div>
      {children}
    </div>
  );
}

function FormDialog({ tab, item, onClose, onSaved }) {
  const schema = SCHEMAS[tab];
  const [form, setForm] = useState(item || schema.initial());
  const [saving, setSaving] = useState(false);
  const isEdit = !!item;

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const url = isEdit && schema.update ? schema.update(item.id) : schema.create;
      const method = isEdit && schema.update ? "put" : "post";
      await api[method](url, form);
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-form-dialog">
      <form
        onSubmit={submit}
        className="bg-white border border-slate-300 w-full max-w-3xl my-8"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="font-display text-2xl">{isEdit ? `Edit ${tab}` : `New ${tab}`}</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100" data-testid="admin-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {tab === "sliders" && (
            <>
              <TriInput testid="form-title" label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              <TriInput testid="form-subtitle" label="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} multiline />
              <Field label="Image URL">
                <ImageUploadField
                  value={form.image_url}
                  onChange={(v) => setForm({ ...form, image_url: v })}
                  testid="form-image-url-slider"
                  required
                />
              </Field>
              <TriInput testid="form-cta-text" label="CTA Button Text" value={form.cta_text} onChange={(v) => setForm({ ...form, cta_text: v })} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="CTA Link">
                  <input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} placeholder="/news" className="w-full border border-slate-300 px-3 py-2" data-testid="form-cta-link" />
                </Field>
                <Field label="Order">
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0) })} className="w-full border border-slate-300 px-3 py-2" data-testid="form-order" />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="form-active" />
                Active (visible on site)
              </label>
            </>
          )}

          {tab === "news" && (
            <>
              <TriInput testid="form-title" label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              <TriInput testid="form-excerpt" label="Excerpt" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} multiline />
              <TriInput testid="form-content" label="Content" value={form.content} onChange={(v) => setForm({ ...form, content: v })} multiline />
              <Field label="Featured Image URL">
                <ImageUploadField
                  value={form.image_url}
                  onChange={(v) => setForm({ ...form, image_url: v })}
                  testid="form-image-url-news"
                />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="form-category">
                    {["General", "Statements", "Humanitarian", "Operations", "Events"].map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </Field>
                <label className="flex items-end gap-2 text-sm pb-2">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="form-published" />
                  Published
                </label>
              </div>
            </>
          )}

          {tab === "gallery" && (
            <>
              <TriInput testid="form-caption" label="Caption" value={form.caption} onChange={(v) => setForm({ ...form, caption: v })} />
              <Field label="Image URL">
                <ImageUploadField
                  value={form.image_url}
                  onChange={(v) => setForm({ ...form, image_url: v })}
                  testid="form-image-url-gallery"
                  required
                />
              </Field>
              <Field label="Category">
                <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="form-category" />
              </Field>
            </>
          )}

          {tab === "events" && (
            <>
              <TriInput testid="form-title" label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              <TriInput testid="form-description" label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} multiline />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Event Date & Time">
                  <input type="datetime-local" value={form.event_date.slice(0, 16)} onChange={(e) => setForm({ ...form, event_date: new Date(e.target.value).toISOString() })} required className="w-full border border-slate-300 px-3 py-2" data-testid="form-event-date" />
                </Field>
                <Field label="Location">
                  <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="form-location" />
                </Field>
              </div>
              <Field label="Image URL">
                <ImageUploadField
                  value={form.image_url}
                  onChange={(v) => setForm({ ...form, image_url: v })}
                  testid="form-image-url-event"
                />
              </Field>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-300 text-sm uppercase tracking-widest" data-testid="admin-form-cancel">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 bg-slate-900 text-white text-sm uppercase tracking-widest hover:bg-red-600 transition flex items-center gap-2 disabled:opacity-60"
            data-testid="admin-form-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function previewLabel(item, tab) {
  const t = item.title?.en || item.caption?.en || "";
  return t || item.id?.slice(0, 8);
}

export default function AdminPage() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("sliders");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(SCHEMAS[tab].list);
      setItems(r.data);
    } catch (e) {
      // 401 is already handled globally (session expired → redirect); skip its toast here.
      if (e?.response?.status !== 401) {
        toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line
  }, [tab, user]);

  if (checking) return <div className="p-10 text-slate-500">Checking session…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" state={{ from: "/admin" }} replace />;

  async function handleDelete(id) {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(SCHEMAS[tab].remove(id));
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="App min-h-screen bg-slate-50" data-testid="admin-page">
      <Toaster richColors position="top-right" />
      <header className="bg-slate-900 text-white border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center">
              <Shield className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-xl tracking-wider">Admin Console</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPwd(true)} className="text-xs uppercase tracking-widest border border-white/30 px-3 py-2 hover:bg-white hover:text-slate-900 flex items-center gap-2" data-testid="admin-change-password">
              <KeyRound className="w-3 h-3" /> Password
            </button>
            <button onClick={() => nav("/")} className="text-xs uppercase tracking-widest border border-white/30 px-3 py-2 hover:bg-white hover:text-slate-900" data-testid="admin-view-site">View site</button>
            <button onClick={() => { logout(); nav("/"); }} className="text-xs uppercase tracking-widest bg-red-600 px-3 py-2 hover:bg-red-700 flex items-center gap-2" data-testid="admin-logout">
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-1 border-b border-slate-300 mb-8" data-testid="admin-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`admin-tab-${tb.id}`}
              className={`px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold border-b-2 -mb-px transition ${
                tab === tb.id ? "border-red-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl text-slate-900 capitalize">{tab}</h1>
          {SCHEMAS[tab].create && (
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="bg-slate-900 text-white px-5 py-2 text-sm uppercase tracking-widest font-semibold hover:bg-red-600 transition flex items-center gap-2"
              data-testid="admin-new-button"
            >
              <Plus className="w-4 h-4" /> New
            </button>
          )}
          {tab === "subscribers" && items.length > 0 && (
            <button
              onClick={() => {
                const csv = "email,name,created_at\n" +
                  items.map((s) => `"${s.email}","${(s.name || "").replace(/"/g, '""')}","${s.created_at}"`).join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `subscribers-${new Date().toISOString().slice(0,10)}.csv`; a.click();
                URL.revokeObjectURL(url);
              }}
              data-testid="admin-export-subscribers"
              className="bg-slate-900 text-white px-5 py-2 text-sm uppercase tracking-widest font-semibold hover:bg-red-600 transition"
            >
              Export CSV
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-slate-500 py-10 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="admin-empty">No items yet.</div>
        ) : tab === "messages" ? (
          <div className="space-y-3" data-testid="admin-list">
            {items.map((m, i) => (
              <div key={m.id} className={`bg-white border ${m.read ? "border-slate-200" : "border-red-600 border-l-4"} p-5`} data-testid={`admin-row-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-display text-xl text-slate-900">{m.subject}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      <span className="font-semibold text-slate-900">{m.name}</span> · {m.email} · {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!m.read && (
                      <button
                        onClick={async () => { await api.put(`/admin/messages/${m.id}/read`); load(); }}
                        data-testid={`admin-mark-read-${i}`}
                        className="px-3 py-1 border border-slate-300 text-xs uppercase tracking-widest hover:bg-slate-900 hover:text-white transition"
                      >
                        Mark read
                      </button>
                    )}
                    <button onClick={() => handleDelete(m.id)} className="p-2 border border-slate-300 hover:bg-red-600 hover:text-white hover:border-red-600" data-testid={`admin-delete-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-line">{m.message}</p>
              </div>
            ))}
          </div>
        ) : tab === "subscribers" ? (
          <div className="bg-white border border-slate-200" data-testid="admin-list">
            {items.map((s, i) => (
              <div key={s.id} className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`admin-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900">{s.email}</div>
                  <div className="text-xs text-slate-500">{s.name || "—"} · {new Date(s.created_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => handleDelete(s.id)} className="p-2 border border-slate-300 hover:bg-red-600 hover:text-white hover:border-red-600" data-testid={`admin-delete-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200" data-testid="admin-list">
            {items.map((it, i) => (
              <div key={it.id} className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`admin-row-${i}`}>
                {it.image_url ? (
                  <img src={it.image_url} alt="" className="w-16 h-16 object-cover bg-slate-100" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-slate-900 truncate">{previewLabel(it, tab)}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {it.category && <span className="mr-3 uppercase tracking-widest text-red-600">{it.category}</span>}
                    {it.event_date && <span className="mr-3">{new Date(it.event_date).toLocaleString()}</span>}
                    {tab === "sliders" && <span>Order #{it.order} · {it.active ? "Active" : "Hidden"}</span>}
                    {tab === "news" && <span>{it.published ? "Published" : "Draft"}</span>}
                  </div>
                </div>
                {SCHEMAS[tab].update && (
                  <button onClick={() => { setEditing(it); setShowForm(true); }} className="p-2 border border-slate-300 hover:border-slate-900" data-testid={`admin-edit-${i}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => handleDelete(it.id)} className="p-2 border border-slate-300 hover:bg-red-600 hover:text-white hover:border-red-600" data-testid={`admin-delete-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <FormDialog
          key={editing?.id || "new"}
          tab={tab}
          item={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {showPwd && <ChangePasswordDialog onClose={() => setShowPwd(false)} />}
    </div>
  );
}
